import React, { Component, createRef, Fragment } from 'react';
import { CloseOutlined, DeleteOutlined, FileTextOutlined, MonitorOutlined, RetweetOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Input, message, Tooltip } from 'antd';
import ConnectionLine from '@/services/Cascade/helper/connectionLine';
import designConstructor from '@/services/helper/designConstructor';
import { strDelimited } from '@/services/helper/split';
import { getTextWidth } from '../../../services/helper/getTextWidth';
import NetsConnectionSetting from './netsConnectionSetting';
import { userDefaultSettings } from '../../../services/userDefaultSetting/userDefaultSettingCtrl';
import { NETS_PINS, NETS_PINNAMES, NONE, BY_NETS, BY_PINS } from '../../../constants/connectionType';
import debounce from '../../../services/helper/debounceFn';
import { getArraySimilar } from '../../../services/helper/arrayHelper';
import { checkConnectCompsPinsAndNets, checkConnectInterposerPinsAndNets } from '../../../services/Cascade/helper/setupData';
import EditableTable from '../../../components/EditableTable';
import { createPortal } from 'react-dom';
import Panel from '../../../components/Panel';
import './index.css';

const SVG = 'svg', TABLE = 'table';
const columns = [
  {
    title: 'Left PCB',
    dataIndex: 'left',
    width: '50%'
  }, {
    title: 'Right PCB',
    dataIndex: 'right',
    width: '50%'
  }
]
class netsConnection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      nets: [],
      netsTable: [],
      selectNets: [],
      save: false,
      sortNets: [],
      open: false,
      search: {},
      displayType: TABLE,
      manualDisplay: false,
      autoConnectionLogs: [],
      logVisible: false
    };
    this.svgRef = createRef();
    this.connectionLine = null;
    this.pointLocation = {}
  }

  componentDidMount() {
    this.props.onRef && this.props.onRef(this);
    const { netsInfo: { nets = [], next = {}, prev = {} }, nets: sortNets = [], autoConnection = [] } = this.props;
    const displayType = this.getDefaultDisplay(next, prev);
    this.setDefaultColumns();
    this.setState({
      nets,
      sortNets: [...nets.flat(), ...sortNets],
      displayType
    }, () => {
      this.connectionLine = new ConnectionLine(this.svgRef.current, this.overClick, this.selectLine);
      this.props.resize && this.props.resize()
      this.setPointLocation(true, autoConnection.length && !nets.length ? true : false);
    })
  }

  componentDidUpdate = (prevProps) => {
    const { drawStatus, autoConnection = [] } = this.props;
    if (prevProps.drawStatus !== drawStatus) {
      const { netsInfo: { nets = [], next = {}, prev = {} }, nets: sortNets = [] } = this.props;
      const { manualDisplay, displayType: prevDisplay } = this.state;
      const displayType = manualDisplay ? prevDisplay : this.getDefaultDisplay(next, prev);
      this.setDefaultColumns();
      this.setState({
        nets,
        sortNets,
        displayType
      }, () => {
        this.connectionLine = new ConnectionLine(this.svgRef.current, this.overClick, this.selectLine);
        this.setPointLocation(true);
      })
    }

    if (autoConnection.length) {
      const { netsInfo } = this.props;
      const { next, prev, nets } = netsInfo;
      const { netsInfo: prevNetsInfo } = prevProps;
      const { next: prevNext, prev: prevPrev } = prevNetsInfo;
      if (((next.comp !== prevNext.comp) || (prev.comp !== prevPrev.comp)) && !nets.length) {
        const { nets: sortNets = [] } = this.props;
        const displayType = this.getDefaultDisplay(next, prev);
        this.setDefaultColumns();
        this.setState({
          nets,
          sortNets,
          displayType
        }, () => {
          this.setPointLocation(false, true);
        })
      }
    }
    if (autoConnection.length !== prevProps.autoConnection.length) {
      const { netsInfo } = this.props;
      const { nets } = netsInfo;
      if (!nets.length) {
        this.setAutoConnectionNets()
      }
    }
  }

  getDefaultDisplay = (next, prev) => {
    const nextCompInfo = next ? next.compInfo : {};
    const nextPins = nextCompInfo && nextCompInfo.pins ? [...nextCompInfo.pins.values()] : [];
    const nextNets = [...new Set(nextPins.map(pin => pin.net))];

    const prevCompInfo = prev ? prev.compInfo : {};
    const prevPins = prevCompInfo && prevCompInfo.pins ? [...prevCompInfo.pins.values()] : [];
    const prevNets = [...new Set(prevPins.map(pin => pin.net))];

    return nextNets.length > 50 || prevNets.length > 50 ? TABLE : SVG;
  }

  getDataSourceNets = () => {
    const { nets } = this.state;
    const leftNets = [...new Set(nets.map(net => net[0]).filter(net => !!net))]
    const rightNets = [...new Set(nets.map(net => net[1]).filter(net => !!net))];

    const baseNets = leftNets.length > rightNets.length ? rightNets : leftNets;
    const baseIndex = leftNets.length > rightNets.length ? 1 : 0;
    const anotherIndex = leftNets.length > rightNets.length ? 0 : 1;
    let newNets = [];
    for (let net of baseNets) {
      const filterNets = [...new Set(nets.filter(n => n[baseIndex] === net).map(n => n[anotherIndex]).filter(n => !!n))];
      newNets.push({
        [baseIndex === 1 ? 'right' : 'left']: [net],
        [anotherIndex === 1 ? 'right' : 'left']: [...filterNets]
      })
    }
    this.setState({
      netsTable: newNets.map((item, index) => ({ index: index + 1, ...item }))
    })
  }

  setDefaultColumns = () => {
    columns[0].title = () => {
      const { netsInfo } = this.props;
      const { next = {} } = netsInfo;
      const { pcb = "" } = next;
      const pcbName = designConstructor.getDesignName(pcb);
      return pcb ? pcbName : 'PCB 1'
    }

    columns[1].title = () => {
      const { netsInfo } = this.props;
      const { prev = {} } = netsInfo;
      const { pcb = "" } = prev;
      const pcbName = designConstructor.getDesignName(pcb);
      return pcb ? pcbName : 'PCB 2'
    }

    columns[0].render = (text) => {
      const { netsInfo } = this.props;
      const { next = {} } = netsInfo;
      const { compInfo, pcbKey } = next;
      const pins = compInfo && compInfo.pins ? [...compInfo.pins.values()] : [];
      const nets = this.getNets(pins, pcbKey);
      return <div className='cascade-net-connection-table-render'>
        {text && Array.isArray(text)
          ? text.map(netKey => {
            const net = nets.find(n => n.net === netKey);
            return net ? net.text : netKey;
          }).join(', ')
          : ''}
      </div>
    }

    columns[1].render = (text, record) => {
      const { netsInfo } = this.props;
      const { prev = {} } = netsInfo;
      const { compInfo, pcbKey } = prev;
      const pins = compInfo && compInfo.pins ? [...compInfo.pins.values()] : [];
      const nets = this.getNets(pins, pcbKey);
      return <div className='cascade-net-connection-table-render'>
        {text && Array.isArray(text)
          ? text.map(netKey => {
            const net = nets.find(n => n.net === netKey);
            return net ? net.text : netKey;
          }).join(', ')
          : ''}
        <CloseOutlined
          className="cascade-net-connection-table-icon"
          onClick={(e) => this.deleteRow(e, record)} />
      </div>
    }

    columns[0].onCell = (record) => {
      const { leftVisible, netsTable } = this.state;
      const { index } = record;
      if (leftVisible !== index) {
        return {
          edit: true,
          record,
          dataIndex: 'left',
          tdClick: (e) => this.openTableCell(e, record, "left")
        }
      }
      const { netsInfo, autoConnection = [] } = this.props;
      const { next = {} } = netsInfo;
      const { compInfo, pcbKey } = next;
      const pins = compInfo && compInfo.pins ? [...compInfo.pins.values()] : [];
      const nets = this.getNets(pins, pcbKey);
      const labels = nets.map(net => ({ key: net.net, label: net.text }))
      const _nets = nets.map(net => net.net)
      const _selected = netsTable.map(row => row['left']).flat(2)
      const selected = [...new Set(_selected)].sort().map(net => ({ type: 'Selected', value: net }))
      const recommend = _nets.filter(net => autoConnection.includes(net) && ![...selected].some(item => item.value === net)).sort().map(net => ({ type: 'Recommend', value: net }))
      const available = _nets.filter(net => ![...recommend, ...selected].some(item => item.value === net)).sort().map(net => ({ type: 'Available', value: net }))
      return {
        record: { ...record },
        edit: 'aurora-select',
        labels,
        options: [...recommend, ...available, ...selected],
        optGroup: ['Recommend', 'Available', 'Selected'],
        dataIndex: 'left',
        selectMode: 'multiple',
        selectButtonTitle: "Select nets",
        maxTagCount: 10,
        onChange: (list, record, setFieldsValue) => this.addNets(list, record, setFieldsValue, 'left'),
        clearSelected: (value, record, setFieldsValue) => this.removeNets(value, record, setFieldsValue, 'left'),
        auroraSelectBlur: () => this.saveNewNets("left"),
        getPopupContainer: document.getElementById('net-connection-select-dialog')
      }
    }

    columns[1].onCell = (record) => {
      const { rightVisible, netsTable } = this.state;
      const { index } = record;
      if (rightVisible !== index) {
        return {
          edit: true,
          record,
          dataIndex: 'right',
          tdClick: (e) => this.openTableCell(e, record, "right")
        }
      }
      const { netsInfo, autoConnection = [] } = this.props;
      const { prev = {} } = netsInfo;
      const { compInfo, pcbKey } = prev;
      const pins = compInfo && compInfo.pins ? [...compInfo.pins.values()] : [];
      const nets = this.getNets(pins, pcbKey);
      const labels = nets.map(net => ({ key: net.net, label: net.text }))
      const _nets = nets.map(net => net.net)
      const _selected = netsTable.map(row => row['right']).flat(2)
      const selected = [...new Set(_selected)].sort().map(net => ({ type: 'Selected', value: net }))
      const recommend = _nets.filter(net => autoConnection.includes(net) && ![...selected].some(item => item.value === net)).sort().map(net => ({ type: 'Recommend', value: net }))
      const available = _nets.filter(net => ![...recommend, ...selected].some(item => item.value === net)).sort().map(net => ({ type: 'Available', value: net }))
      return {
        record: { ...record },
        edit: 'aurora-select',
        labels,
        options: [...recommend, ...available, ...selected],
        optGroup: ['Recommend', 'Available', 'Selected'],
        dataIndex: 'right',
        selectMode: 'multiple',
        selectButtonTitle: "Select nets",
        maxTagCount: 10,
        onChange: (list, record, setFieldsValue) => this.addNets(list, record, setFieldsValue, 'right'),
        clearSelected: (value, record, setFieldsValue) => this.removeNets(value, record, setFieldsValue, 'right'),
        auroraSelectBlur: () => this.saveNewNets("right"),
        getPopupContainer: document.getElementById('net-connection-select-dialog')
      }
    }
  }

  addNets = (list, record, setFieldsValue, type) => {
    const { netsTable } = this.state;
    let newTableData = [...netsTable];
    const currentRow = newTableData.find(item => item.index === record.index);
    let netsList = record[type];
    if (list.length === 1 && netsList.find(pin => pin === list[0])) {
      netsList = netsList.filter(pin => pin !== list[0]);
    } else {
      netsList = [...new Set([...netsList, ...list])];
    }
    if (setFieldsValue) {
      setFieldsValue({ [type]: [...netsList] })
    }
    currentRow[type] = netsList;
    this.setState({
      netsTable: newTableData
    })
  }

  removeNets = (value, record, setFieldsValue, type) => {
    const { netsTable } = this.state;
    let newTableData = [...netsTable];
    const currentRow = newTableData.find(item => item.index === record.index);
    let netsList = record[type];
    netsList = netsList.filter(net => net !== value);
    if (setFieldsValue) {
      setFieldsValue({ [type]: [...netsList] })
    }
    currentRow[type] = netsList;
    this.setState({
      netsTable: newTableData
    })
  }

  deleteRow = (e, record) => {
    e && e.stopPropagation()
    const { netsTable } = this.state;
    const newTableData = [...netsTable].filter(row => row.index !== record.index).map((row, index) => ({ ...row, index: index + 1 }));
    this.setState({
      netsTable: newTableData
    }, () => {
      this.saveNewNets()
    })
  }

  addNewRow = () => {
    const { netsTable } = this.state;
    const newTableData = [...netsTable, { left: [], right: [] }].map((row, index) => ({ ...row, index: index + 1 }));
    this.setState({
      netsTable: newTableData
    })
  }

  saveNewNets = (type) => {
    if (type) {
      this.setState({
        [`${type}Visible`]: false
      })
    }
    const { netsTable } = this.state;
    let nets = [];
    for (let row of netsTable) {
      const { left = [], right = [] } = row;
      for (let lNet of left) {
        for (let rNet of right) {
          nets.push([lNet, rNet])
        }
      }
    }
    this.setState({
      nets,
      save: true
    }, () => {
      this.props.saveNets && this.props.saveNets()
    })
  }

  openConnectionSetting = (e) => {
    e && e.stopPropagation();
    this.setState({
      open: true,
      prevAutoType: this.getAutoMatch()
    })
  }

  switchDisplayType = () => {
    this.setState({
      displayType: this.state.displayType === TABLE ? SVG : TABLE,
      manualDisplay: true
    }, () => {
      if (this.state.displayType === SVG) {
        this.connectionLine = new ConnectionLine(this.svgRef.current, this.overClick, this.selectLine);
      }
      this.props.resize && this.props.resize()
      setTimeout(() => {
        this.setState({
          manualDisplay: false
        })
      }, 3000)
    })
  }

  openTableCell = (e, record, type) => {
    let visible = this.state[`${type}Visible`];

    if (visible === record.index) {
      return;
    }
    this.setState({
      [`${type}Visible`]: record.index
    })
  }

  closeConnectionSetting = () => {
    const { prevAutoType } = this.state;
    const { netsInfo: { nets = [] }, autoConnection = [] } = this.props;
    this.setState({
      open: false
    }, () => {
      const autoType = this.getAutoMatch();
      this.setPointLocation(true, !prevAutoType && autoType && !nets.length && autoConnection.length ? true : false);
    })
  }

  searchNets = (e, key) => {
    const value = e.target.value;
    const { search } = this.state;
    this.setState({
      search: {
        ...search,
        [key]: value
      }
    }, () => {
      debounce(() => {
        this.connectionLine && this.connectionLine.removeAllLines();
        this.setPointLocation(true);
      }, 500, false, 'connectionNetKey')()
    })
  }

  getNetsAndSave = () => {
    const { nets, save } = this.state;
    const { netsInfo } = this.props;
    return { nets: nets.filter(net => net[0] && net[1]), save, netsInfo }
  }

  getNets = (pins, pcbKey) => {
    const { sortNets, search } = this.state;
    const pcbSearch = search[pcbKey]
    const nets = [...new Set(pins.map(i => i.net))].sort((a, b) => !sortNets.includes(a) && sortNets.includes(b) ? 1 : -1);
    const userSetting = userDefaultSettings.getLocalSetting();
    const { cascadeSettings = {} } = userSetting;
    const { connectionDisplay = NETS_PINS } = cascadeSettings;
    return nets.map(net => {
      const _pins = pins.filter(p => p.net === net);
      let pinText = connectionDisplay === NETS_PINS ? _pins.map(p => p.pin) : connectionDisplay === NETS_PINNAMES ? _pins.map(p => p.pinName) : [];
      if (pcbSearch) {
        pinText = pinText.sort((a, b) => !a.toLowerCase().includes(pcbSearch.toLowerCase()) && b.toLowerCase().includes(pcbSearch.toLowerCase()) ? 1 : -1);
      }
      if (pinText && pinText.length > 3) {
        pinText = pinText.slice(0, 3);
        pinText = `${pinText.join(', ')}...`
      } else {
        pinText = pinText.join(', ')
      }
      return { net, text: !pinText ? net : `${net} ( ${pinText} )` }
    }).filter(netInfo => !pcbSearch || netInfo.text.toLowerCase().includes(pcbSearch.toLowerCase()))
  }

  getAutoMatch = () => {
    const userSetting = userDefaultSettings.getLocalSetting();
    const { cascadeSettings = {} } = userSetting;
    const { autoSelectConnection = NONE } = cascadeSettings;
    return autoSelectConnection === NONE ? false : autoSelectConnection
  }

  showNetsRender = ({ comp, compInfo, pcbKey, type, pcb }) => {
    if (!compInfo) {
      console.log({ comp, compInfo, pcbKey, type, pcb })
    }
    let pins = compInfo && compInfo.pins ? [...compInfo.pins.values()] : [];
    const pcbName = designConstructor.getDesignName(pcb);
    const nets = this.getNets(pins, pcbKey);
    const autoType = this.getAutoMatch();
    const selectNets = this.state.nets.flat()
    const autoConnection = this.props.autoConnection || []
    return (
      <div className='cascade-net-connection-column-content'>
        <div className='cascade-net-connection-column-content-comp' title={pcbName}>{pcbName}</div>
        <div className='cascade-net-connection-column-content-comp' title={comp}>{comp}</div>
        <Input className='cascade-net-connection-column-content-input' addonBefore={<SearchOutlined />} onChange={(e) => this.searchNets(e, pcbKey)} size='small' />
        {nets.map(netInfo => {
          const { net, text } = netInfo;
          const width = `calc(50% - ${getTextWidth(text) / 2 + 15}px)`;
          return (
            <div className='cascade-net-connection-column-content-net' key={`${pcbKey}-${net}`}>
              <span className={`dashed`} style={type === 'prev' ? { width, left: 0 } : { width, right: 0 }} />
              <span className='cascade-net-connection-row-box'>
                {type === 'next' && autoType && <MonitorOutlined
                  className='cascade-net-connection-column-content-auto-match-icon cascade-net-connection-column-content-auto-match-icon-next'
                  onClick={(e) => this.autoMatchLine(e, `${type}@${pcbKey}@${comp}@${net}`)} />}
                <span className={[...selectNets, ...autoConnection].includes(net) ? 'cascade-connection-nets-selected' : ''}>{text}</span>
                {type === 'prev' && autoType && <MonitorOutlined
                  className='cascade-net-connection-column-content-auto-match-icon cascade-net-connection-column-content-auto-match-icon-prev'
                  onClick={(e) => this.autoMatchLine(e, `${type}@${pcbKey}@${comp}@${net}`)} />}
              </span>
              {<div
                className={`${type}-button connection-net-button`}
                id={`${type}@${pcbKey}@${comp}@${net}`}
                onClick={(e) => this.clickLine(e, `${type}@${pcbKey}@${comp}@${net}`)}
              ></div>}
            </div>
          );
        })}
      </div>
    );
  }

  clickLine = (e, key) => {
    e && e.stopPropagation();
    this.connectionLine.saveTempPosition(key);
    this.setState({
      drawLine: true
    }, () => {
      this.connectionLine.drawTempLine(key)
    })
  }

  autoMatchLine = (e, key) => {
    e && e.stopPropagation();
    const { netsInfo } = this.props;
    const [type, pcbKey, comp, net] = key.split('@');
    const anotherType = type === 'prev' ? 'next' : 'prev';
    const _compInfo = netsInfo ? netsInfo[anotherType] : null;
    const c_compInfo = netsInfo ? netsInfo[type] : null;
    if (!_compInfo || !c_compInfo) {
      message.error('No Nets found to automatically match.')
      return;
    }
    const { pcb, comp: anotherComp, compInfo, pcbKey: anotherKey } = _compInfo;
    let matchNets = [];
    const autoType = this.getAutoMatch();
    if (autoType === BY_NETS) {
      const pins = compInfo && compInfo.pins ? [...compInfo.pins.values()] : [];
      const nets = [...new Set(pins.map(i => i.net))];
      if (!nets.length) {
        message.error('No Nets found to automatically match.')
        return;
      }
      const netsSimilar = nets.map(_net => ({ net: _net, similar: getArraySimilar(net, _net) })).filter(_net => _net.similar > 0.5).sort((a, b) => b.similar - a.similar);
      if (!netsSimilar.length) {
        message.error('No Nets found to automatically match.')
        return;
      }
      matchNets = [netsSimilar[0].net]
    } else if (autoType === BY_PINS) {
      const { pcb: c_pcb } = c_compInfo;
      const { nets } = checkConnectCompsPinsAndNets({ designId: c_pcb, name: comp, nets: [net] }, { designId: pcb, name: anotherComp })
      matchNets = [...nets];
    }

    if (!matchNets.length) {
      message.error('No Nets found to automatically match.')
      return;
    }

    let _nets = this.state.nets;
    for (let matchNet of matchNets) {
      const _key = `${anotherType}@${anotherKey}@${anotherComp}@${matchNet}`
      const ele = document.getElementById(`line@${key}@${_key}`);
      const _ele = document.getElementById(`line@${_key}@${key}`);
      if (!ele && !_ele) {
        const start = this.pointLocation[key] || null;
        const end = this.pointLocation[_key] || null;
        if (!start || !end) {
          message.error(`Net ${matchNet} draw failed.`)
          continue;
        }
        this.connectionLine.drawLines(`line@${key}@${_key}`, { x: type === 'next' ? start.x + 8 : start.x, y: start.y, _x: type === 'next' ? end.x : end.x + 8, _y: end.y })
        const newNet = type === 'next' ? [net, matchNet] : [matchNet, net];
        _nets.push(newNet);
      }
    }

    this.setState({
      nets: _nets,
      save: true
    }, () => {
      this.props.saveNets && this.props.saveNets()
    })
  }

  setAutoConnectionNets = () => {
    const { netsInfo, autoConnection, hasInterposer, hasPassive } = this.props;
    let type = 'next', anotherType = 'prev';
    let _compInfo = netsInfo ? netsInfo[anotherType] : null;
    let c_compInfo = netsInfo ? netsInfo[type] : null;
    if (!_compInfo || !c_compInfo) {
      return;
    }
    let nets = [];
    const prevPins = c_compInfo && c_compInfo.compInfo && c_compInfo.compInfo.pins ? [...c_compInfo.compInfo.pins.values()] : [];
    const prevNets = [...new Set(prevPins.map(item => item.net).filter(net => autoConnection.includes(net)))];
    nets = [...prevNets]
    const nextPins = _compInfo && _compInfo.compInfo && _compInfo.compInfo.pins ? [..._compInfo.compInfo.pins.values()] : [];
    const nextNets = [...new Set(nextPins.map(item => item.net).filter(net => autoConnection.includes(net)))];
    if (nextNets.length > prevNets.length) {
      type = 'prev';
      anotherType = 'next';
      _compInfo = netsInfo ? netsInfo[anotherType] : null;
      c_compInfo = netsInfo ? netsInfo[type] : null;
      nets = [...nextNets]
    }

    const { pcb, comp, pcbKey } = c_compInfo;
    const { pcb: anotherPcb, comp: anotherComp, compInfo: anotherCompInfo, pcbKey: anotherKey } = _compInfo;
    let _nets = [];
    this.removeAllLines();

    if (!comp || !anotherComp) {
      return;
    }

    const { displayType } = this.state;
    let autoConnectionLogs = [];
    const autoType = this.getAutoMatch();
    autoConnectionLogs.push(`Auto connection type: ${autoType}`);
    if (hasInterposer) {
      autoConnectionLogs.push(`Parse Interposer's pins, output format: [Pin Number, another information]`);
    }
    for (let net of nets) {
      let matchNet = [];
      if (autoType === BY_NETS) {
        const pins = anotherCompInfo && anotherCompInfo.pins ? [...anotherCompInfo.pins.values()] : [];
        const nets = [...new Set(pins.map(i => i.net))];
        if (!nets.length) {
          continue;
        }
        const netsSimilar = nets.map(_net => ({ net: _net, similar: getArraySimilar(net, _net) })).filter(_net => _net.similar > 0.5).sort((a, b) => b.similar - a.similar);
        autoConnectionLogs.push(`Net: ${net}`);
        if (!netsSimilar.length) {
          autoConnectionLogs.push(` - No similar nets found`);
          continue;
        }
        matchNet = [netsSimilar[0].net]
        autoConnectionLogs.push(` - Similar net fouund: ${netsSimilar[0].net}, similarity: ${netsSimilar[0].similar * 100}%`);
      } else if (autoType === BY_PINS) {
        autoConnectionLogs.push(`Net: ${net}`);
        if (hasInterposer || hasPassive) {
          const { nets = [], logs = [] } = checkConnectInterposerPinsAndNets({ designId: pcb, name: comp, nets: [net] }, { designId: anotherPcb, name: anotherComp })
          matchNet.push(...nets)
          autoConnectionLogs.push(...logs)
        } else {
          const { nets = [], logs = [] } = checkConnectCompsPinsAndNets({ designId: pcb, name: comp, nets: [net] }, { designId: anotherPcb, name: anotherComp })
          if (nets.length) {
            matchNet = [nets[0]];
          }
          autoConnectionLogs.push(...logs)
        }
      }

      if (matchNet.length) {
        for (let mNet of matchNet) {
          if (displayType === TABLE) {
            const newNet = type === 'next' ? [net, mNet] : [mNet, net];
            _nets.push(newNet);
            continue;
          }
          const _key = `${anotherType}@${anotherKey}@${anotherComp}@${mNet}`
          const key = `${type}@${pcbKey}@${comp}@${net}`
          const ele = document.getElementById(`line@${key}@${_key}`);
          const _ele = document.getElementById(`line@${_key}@${key}`);
          if (!ele && !_ele) {
            const start = this.pointLocation[key] || null;
            const end = this.pointLocation[_key] || null;
            if (!start || !end) {
              message.error(`Net ${mNet} draw failed.`)
              continue;
            }
            this.connectionLine.drawLines(`line@${key}@${_key}`, { x: type === 'next' ? start.x + 8 : start.x, y: start.y, _x: type === 'next' ? end.x : end.x + 8, _y: end.y })
            const newNet = type === 'next' ? [net, mNet] : [mNet, net];
            _nets.push(newNet);
          }
        }
      }
    }
    this.setState({
      nets: _nets,
      sortNets: _nets.flat(),
      autoConnectionLogs,
      save: true
    }, () => {
      this.props.saveNets && this.props.saveNets();
      this.setPointLocation(true);
      if (this.state.displayType === TABLE) {
        this.getDataSourceNets()
      }
    })
  }

  overClick = (key, endX, endY) => {
    this.setState({
      drawLine: false
    })
    const { nets } = this.state;
    let _nets = [...nets];
    const keys = Object.keys(this.pointLocation);
    const [type, pcbKey, comp, net] = key.split('@');
    const start = this.pointLocation[key] || {}
    let x = start.x || 0, y = start.y || 0;
    for (let _key of keys) {
      let value = this.pointLocation[_key];
      const { x: _x, y: _y } = value;
      if (key === _key) {
        continue;
      }
      if (Math.abs(endX - _x) <= 10 && Math.abs(endY - _y) <= 10) {
        const [_type, _pcbKey, _comp, _net] = _key.split('@');
        if (_type !== type && _pcbKey !== pcbKey) {
          const ele = document.getElementById(`line@${key}@${_key}`);
          const _ele = document.getElementById(`line@${_key}@${key}`);
          if (!ele && !_ele) {
            this.connectionLine.drawLines(`line@${key}@${_key}`, { x: type === 'next' ? x + 8 : x, y, _x: type === 'next' ? _x : _x + 8, _y })
            const newNet = type === 'next' ? [net, _net] : [_net, net];
            _nets.push(newNet);
          }
        }
      }
    }
    this.setState({
      nets: _nets,
      save: true
    }, () => {
      this.props.saveNets && this.props.saveNets()
    })
  }

  selectLine = (key) => {
    const { selectNets } = this.state;
    const _key = key.replace(/[^A-Za-z0-9]/ig, '');
    const ele = document.getElementById(_key);
    if (selectNets.includes(key)) {
      ele.classList.remove('cascade-nets-select')
      this.setState({
        selectNets: selectNets.filter(i => i !== key)
      })
    } else {
      ele.classList.add('cascade-nets-select')
      this.setState({
        selectNets: [...selectNets, key]
      })
    }
  }

  setPointLocation = (redraw = false, auto = false) => {
    const { displayType } = this.state;
    if (displayType === SVG) {
      const points = document.getElementsByClassName('connection-net-button');
      this.pointLocation = {};
      if (points) {
        for (let point of points) {
          let y = point.offsetTop, x = point.offsetLeft;
          let _point = point;
          while (point.offsetParent) {
            _point = _point.offsetParent;
            x = x + _point.offsetLeft;
            y = y + _point.offsetTop;
            if (_point.id === "cascade-net-connection-content") {
              break;
            }
          }
          this.pointLocation[point.id] = { x: x - 10, y: y - 10 }
        }
      }
      redraw && this.redrawLine();
    } else if (displayType === TABLE) {
      this.getDataSourceNets()
    }
    auto && this.setAutoConnectionNets()
  }

  redrawLine = () => {
    const { nets } = this.state;
    const { netsInfo } = this.props;
    const { next, prev } = netsInfo;
    for (let i = 0; i < nets.length; i++) {
      const netGroup = nets[i];
      const [nextNet, prevNet] = netGroup;
      const id = `next@${next.pcbKey}@${next.comp}@${nextNet}`;
      let location = this.pointLocation[id];
      const _id = `prev@${prev.pcbKey}@${prev.comp}@${prevNet}`;
      let nextLocation = this.pointLocation[_id];
      this.connectionLine.removeLine(`line@${_id}@${id}`);
      if (!location || !nextLocation) {
        continue;
      }
      if (nextLocation) {
        this.connectionLine.drawLines(`line@${id}@${_id}`, { x: location.x + 8, y: location.y, _x: nextLocation.x, _y: nextLocation.y })
      }
    }
  }

  removeNetLine = () => {
    const { selectNets, nets } = this.state;
    let _nets = [...nets];
    for (let key of selectNets) {
      const pNet = strDelimited(key, '@', { returnIndex: 4 })
      const nNet = strDelimited(key, '@', { returnIndex: 8 })
      _nets = _nets.filter(item => !(item[0] === pNet && item[1] === nNet) && !(item[0] === nNet && item[1] === pNet));
      this.connectionLine.removeLine(key);
    }
    this.setState({
      nets: _nets,
      selectNets: [],
      save: true
    }, () => {
      this.props.saveNets && this.props.saveNets()
    })
  }

  clearConnectionNets = () => {
    this.removeAllLines();
    this.setState({
      nets: [],
      selectNets: [],
      netsTable: [],
      save: true
    }, () => {
      this.props.saveNets && this.props.saveNets()
    })
  }

  clearNetsInfo = () => {
    this.removeAllLines();
    this.setState({
      nets: [],
      selectNets: [],
      netsTable: [],
      save: true
    }, () => {
      this.setAutoConnectionNets()
    })
  }

  changeLogVisible = (e, visible) => {
    e && e.stopPropagation();
    this.setState({
      logVisible: visible
    })
  }

  removeAllLines = () => {
    this.connectionLine && this.connectionLine.removeAllLines();
  }

  autoSearchTooltip = () => {
    const autoType = this.getAutoMatch();
    if (!autoType) {
      return null;
    }
    return <span className='cascade-connection-auto-search-tooltip'>
      Click <MonitorOutlined /> button next to net to connect automatically. The current rule is
      <Tooltip classNames={{ root: 'aurora-tooltip' }} title="Click the setting button in the upper right corner of the panel to change the rule." className='cascade-connection-auto-search-tooltip-type'> {autoType}</Tooltip>.
    </span>
  }

  svgRender = () => {
    const { drawLine, selectNets } = this.state;
    const { netsInfo, contentHeight, contentWidth } = this.props;
    const { next = {}, prev = {} } = netsInfo;
    return <div className="cascade-connection-content cascade-net-connection-content" id="cascade-net-connection-content">
      <div className="cascade-connection-column cascade-net-connection-column">
        {this.showNetsRender(next)}
      </div>
      <div className="cascade-connection-column cascade-net-connection-column">
        {this.showNetsRender(prev)}
      </div>
      {selectNets.length ? <span className="cascade-net-connection-delete" onClick={this.removeNetLine}>Delete Connection</span> : null}
      <svg ref={this.svgRef} className={`plot ${drawLine ? 'cascade-connect-draw-line-status' : ''}`} style={{ width: contentWidth - 20, height: contentHeight - 20 }}></svg>
    </div>
  }

  tableRender = () => {
    const { netsTable } = this.state;
    return <div className="cascade-connection-content cascade-net-connection-content cascade-net-connection-table-body" id="cascade-net-connection-content">
      <EditableTable
        rowKey={(record) => record.index}
        tableLayout={"fixed"}
        columns={columns}
        size="small"
        dataSource={netsTable}
        tablePadding={true}
        className="cascade-net-connection-table"
      />
      <Button className='cascade-net-connection-table-add-button' onClick={this.addNewRow} type="text" size='small'>+ Add new table row</Button>
    </div>
  }

  logPanelRender = () => {
    const { autoConnectionLogs = [] } = this.state;
    const content = (
      <Fragment>
        <Panel
          className='library-data-panel'
          position='panel-center-left'
          title={`Automatic matching log`}
          zIndex={2000}
          onCancel={() => { this.changeLogVisible(null, false) }}
          draggable
          minHeight={200}
          maxHeight={800}
          width={800}
        >
          <div className="cascade-net-connection-log-panel">
            <div className="cascade-display-detail-box">
              {
                autoConnectionLogs.length ? autoConnectionLogs.map((log, index) => (
                  <span className={log.includes('Net:') ? 'aurora-msg-bold' : ''} key={index}>
                    {log}
                  </span>
                )) : <span>No automatic matching log found.</span>
              }
            </div>
          </div>
        </Panel>
      </Fragment>
    )
    return createPortal(content, document.getElementById('root'))
  }

  render() {
    const { open, displayType, autoConnectionLogs = [], logVisible } = this.state;
    return (
      <Fragment>
        {displayType === SVG && this.svgRender()}
        {displayType === TABLE && this.tableRender()}
        {this.autoSearchTooltip()}
        <div className="cascade-connection-nets-setting">
          {autoConnectionLogs.length ? <FileTextOutlined title='Automatic matching log' onClick={(e) => this.changeLogVisible(e, true)} /> : null}
          <RetweetOutlined title='Switch net connection display type' onClick={this.switchDisplayType} />
          <DeleteOutlined title='Clear all connections' onClick={this.clearConnectionNets} />
          <MonitorOutlined title='Automatically find connections' onClick={this.setAutoConnectionNets} />
          <SettingOutlined onClick={this.openConnectionSetting} />
        </div>
        {open && <NetsConnectionSetting closeModal={this.closeConnectionSetting} />}
        {logVisible && this.logPanelRender()}
        <div id="net-connection-select-dialog"></div>
      </Fragment>
    );
  }
}


export default netsConnection;
